import { Check, Copy } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/** How long the copied/failed state stays before the chip goes quiet again. */
const FEEDBACK_MS = 1600;

/** What a screen reader hears when the chip answers. */
const ANNOUNCEMENTS = {
    idle: '',
    copied: 'Copied',
    failed: 'Could not copy',
} as const;

interface CopyableHashProps {
    /** The WHOLE hash. What is copied is never what is displayed. */
    value: string;
    /** Prefix shown before the hash — "TX", "Wallet", … */
    label?: string;
    /** Characters kept at the head and tail of the shortened form. */
    lead?: number;
    tail?: number;
    className?: string;
}

/**
 * A truncated hash that can be copied whole.
 *
 * A transaction hash exists to be pasted into an explorer, and a shortened one
 * printed as plain text can only be transcribed by hand — so this reads as
 * something you press: its own surface, and a copy affordance that answers when
 * you do.
 */
export const CopyableHash: React.FC<CopyableHashProps> = ({
    value,
    label = 'TX',
    lead = 10,
    tail = 8,
    className,
}) => {
    const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (timer.current) clearTimeout(timer.current);
        },
        [],
    );

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setState('copied');
        } catch {
            // No clipboard permission, or an insecure context. Say so rather
            // than flashing a success the paste will not honour.
            setState('failed');
        }
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setState('idle'), FEEDBACK_MS);
    };

    const short =
        value.length > lead + tail
            ? `${value.slice(0, lead)}…${value.slice(-tail)}`
            : value;

    return (
        <button
            type="button"
            onClick={() => void copy()}
            aria-label={`Copy ${label} ${value}`}
            className={cn(
                'group/copy inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-secondary/60 px-2 py-1 font-mono text-[10px] tabular-nums text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                className,
            )}
        >
            <span>
                {label}: {short}
            </span>
            {state === 'copied' ? (
                <Check size={11} className="shrink-0 text-success" />
            ) : (
                <Copy
                    size={11}
                    className={cn(
                        'shrink-0 transition-opacity',
                        state === 'failed'
                            ? 'text-destructive opacity-100'
                            : 'opacity-50 group-hover/copy:opacity-100',
                    )}
                />
            )}
            <span aria-live="polite" className="sr-only">
                {ANNOUNCEMENTS[state]}
            </span>
        </button>
    );
};

export default CopyableHash;
